import { useState, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { 
  X,
  TrendingUp,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  User,
  Settings,
  LogOut,
  Building2,
  Brain
} from 'lucide-react'
import clsx from 'clsx'
import { navigation, clientNavigation, vendorNavigation, superAdminNavigation } from '../config/navigationConfig'
import { useAuth } from '../contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { clientsAPI } from '../services/api'
import BrandLogo from './BrandLogo'

const secondaryNavigation = []

export default function Navigation({ isOpen, onClose, isCollapsed, toggleCollapse, mobileOnly = false }) {
  const location = useLocation()
  const { hasAnyPermission, user, logout, activePortal, enabledModules, isSuperAdmin } = useAuth()
  const [expandedMenus, setExpandedMenus] = useState({})
  const [searchQuery, setSearchQuery] = useState('')

  // Fetch Tenant Profile for Custom Logo Branding
  const { data: tenantRes } = useQuery({
    queryKey: ['my-business-profile'],
    queryFn: () => clientsAPI.getMyBusinessProfile(),
    enabled: !!(user?.tenant_id || user?.type === 'SuperAdmin'),
    staleTime: 60000
  })

  const tenantProfile = tenantRes?.data?.data
  const tenantLogo = tenantProfile?.logo_url || user?.tenant_logo
  const tenantName = tenantProfile?.name || tenantProfile?.company_name || user?.tenant_name || 'Global Enterprise'
  const brandColor = tenantProfile?.brand_color || '#2563eb'

  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await logout()
    }
  }

  // Select navigation based on active portal and role
  const activeNavigation = useMemo(() => {
    if (isSuperAdmin || user?.type === 'SuperAdmin' || activePortal === 'superadmin' || location.pathname.startsWith('/superadmin')) {
      return superAdminNavigation
    }
    if (activePortal === 'vendor' || location.pathname.startsWith('/vendor')) {
      return vendorNavigation
    }
    return clientNavigation
  }, [activePortal, isSuperAdmin, user?.type, location.pathname])

  // Initialize expanded state based on current path
  useEffect(() => {
    const expandActivePaths = (items, parentId = '') => {
      items.forEach(item => {
        const itemId = parentId ? `${parentId}-${item.name}` : item.name
        
        if (item.children) {
          const hasActiveChild = item.children.some(child => 
            child.href === location.pathname || 
            (child.children && child.children.some(grandchild => grandchild.href === location.pathname))
          )
          
          if (hasActiveChild) {
            setExpandedMenus(prev => ({ ...prev, [itemId]: true }))
          }
          
          expandActivePaths(item.children, itemId)
        }
      })
    }

    expandActivePaths(activeNavigation)
  }, [location.pathname, activeNavigation])

  // Filter navigation based on permissions and enabled modules
  const filteredNavigation = useMemo(() => {
    if (isSuperAdmin || user?.type === 'SuperAdmin' || activePortal === 'superadmin' || location.pathname.startsWith('/superadmin')) {
      return superAdminNavigation
    }

    const filterItems = (items) => {
      return items.reduce((acc, item) => {
        // Check if user has permission for this item
        const hasAccess = !item.permissions || item.permissions.length === 0 || hasAnyPermission(item.permissions)
        
        // Check if module is enabled
        const isModuleEnabled = !item.module || (enabledModules && enabledModules.includes(item.module))

        if (item.children) {
          // Recursively filter children
          const filteredChildren = filterItems(item.children)
          
          // If item has children, show it if it has visible children OR if it has direct access and is a link itself
          if (filteredChildren.length > 0 && isModuleEnabled) {
            acc.push({ ...item, children: filteredChildren })
          } else if (hasAccess && isModuleEnabled && item.href && item.href !== '#') {
             acc.push({ ...item, children: [] })
          }
        } else {
          // Leaf item
          if (hasAccess && isModuleEnabled) {
            acc.push(item)
          }
        }
        return acc
      }, [])
    }
    
    return filterItems(activeNavigation)
  }, [hasAnyPermission, activeNavigation, enabledModules, isSuperAdmin, user?.type])

  const isActive = (href) => {
    if (href === '/dashboard') {
      return location.pathname === href
    }
    return location.pathname.startsWith(href)
  }

  // Auto-expand menus based on current route
  useEffect(() => {
    const allNavItems = [...activeNavigation, ...secondaryNavigation]
    
    const checkExpand = (items) => {
      items.forEach(item => {
        if (item.children) {
          const isDirectChildActive = item.children.some(child => isActive(child.href))
          const isGrandChildActive = item.children.some(child => 
            child.children && child.children.some(grandChild => isActive(grandChild.href))
          )

          if (isDirectChildActive || isGrandChildActive || isActive(item.href)) {
            setExpandedMenus(prev => ({ ...prev, [item.name]: true }))
          }

          if (isGrandChildActive) {
             item.children.forEach(child => {
                if (child.children && child.children.some(gc => isActive(gc.href))) {
                   setExpandedMenus(prev => ({ ...prev, [child.name]: true }))
                }
             })
          }
        }
      })
    }
    
    checkExpand(allNavItems)
  }, [location.pathname])

  const handleMenuClick = (item) => {
    if (item.children) {
      if (isCollapsed) {
        toggleCollapse()
        setTimeout(() => {
            setExpandedMenus(prev => ({ ...prev, [item.name]: true }))
        }, 150)
      } else {
        setExpandedMenus(prev => ({ ...prev, [item.name]: !prev[item.name] }))
      }
    } else {
      onClose()
    }
  }

  // Flatten all navigation items for search
  const flattenItems = (items) => {
    return items.flatMap(item => {
      if (item.children) {
        return [item, ...flattenItems(item.children)]
      }
      return [item]
    })
  }

  const allItems = [
    ...flattenItems(activeNavigation),
    ...flattenItems(secondaryNavigation)
  ]

  const filteredItems = searchQuery
    ? allItems.filter(item => 
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : []

  return (
    <>
      {/* Mobile sidebar overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-20 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={clsx(
        "fixed inset-y-0 left-0 z-30 bg-white shadow-xl transform transition-all duration-300 ease-in-out",
        mobileOnly ? "lg:hidden" : "lg:translate-x-0",
        isOpen ? "translate-x-0" : "-translate-x-full",
        isCollapsed ? "w-20" : "w-64"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo Section: Sanglob Global AI ERP */}
          <div className={clsx(
            "flex items-center h-16 border-b border-slate-800 bg-[#0f172a] text-white transition-all duration-300",
            isCollapsed ? "justify-center px-0" : "justify-between px-4"
          )}>
            <div className={clsx("flex items-center", isCollapsed ? "justify-center" : "")}>
              <BrandLogo 
                className={clsx("object-contain transition-all", isCollapsed ? "h-4.5" : "h-5.5 sm:h-6")} 
                dark={true}
                showBySanglob={!isCollapsed}
                bySanglobClassName="text-[7.5px] text-slate-400 tracking-wider"
              />
            </div>
            <button
              onClick={onClose}
              className="lg:hidden p-1.5 rounded-md text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Main Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto overflow-x-hidden stylish-scrollbar">
            
            {/* Search Box */}
            {!isCollapsed && (
              <div className="px-3 mb-6">
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                  </div>
                  <input
                    type="text"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg leading-5 bg-gray-50 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-400 sm:text-sm transition-all"
                    placeholder="Search modules..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <X className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-pointer" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {searchQuery && !isCollapsed ? (
              /* Search Results */
              <div className="space-y-1 animate-in fade-in slide-in-from-top-2 duration-200">
                <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Search Results
                </h3>
                {filteredItems.length > 0 ? (
                  filteredItems.map((item) => {
                    const Icon = item.icon
                    const active = isActive(item.href)
                    
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => {
                          onClose()
                          setSearchQuery('')
                        }}
                        className={clsx(
                          "flex items-center px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 group relative",
                          active
                            ? "bg-blue-50 text-blue-700"
                            : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                        )}
                      >
                        <Icon className={clsx(
                          "w-5 h-5 flex-shrink-0 mr-3",
                          active ? "text-blue-600" : "text-gray-400 group-hover:text-gray-600"
                        )} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium whitespace-nowrap">{item.name}</div>
                          {item.description && !item.hideDescriptionInSidebar && (
                            <div className="text-xs text-gray-500 mt-0.5 truncate">{item.description}</div>
                          )}
                        </div>
                      </Link>
                    )
                  })
                ) : (
                  <div className="px-3 py-8 text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
                      <Search className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-900 font-medium">No modules found</p>
                    <p className="text-xs text-gray-500 mt-1">Try searching for something else</p>
                  </div>
                )}
              </div>
            ) : (
              /* Standard Navigation */
              <div className="space-y-1">
                {filteredNavigation.map((item) => {
                    const Icon = item.icon
                    const active = isActive(item.href)
                    const isExpanded = expandedMenus[item.name]
                    
                    if (item.children) {
                      const isChildActive = item.children.some(child => {
                        if (child.children) {
                          return child.children.some(gc => isActive(gc.href))
                        }
                        return isActive(child.href)
                      })
                      // Use main menu active style if any child is active or parent is active
                      const isParentActive = active || isChildActive
                      
                      return (
                        <div key={item.name} className="space-y-1">
                          <button
                            onClick={() => handleMenuClick(item)}
                            title={isCollapsed ? item.name : undefined}
                            className={clsx(
                              "w-full flex items-center py-3 text-sm font-medium rounded-xl transition-all duration-200 group relative",
                              isCollapsed ? "justify-center px-0" : "px-3",
                              isParentActive
                                ? "bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 border-r-4 border-blue-600 shadow-sm"
                                : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                            )}
                          >
                            <Icon className={clsx(
                              "w-5 h-5 flex-shrink-0 transition-colors",
                              !isCollapsed && "mr-3",
                              isParentActive ? "text-blue-600" : "text-gray-400 group-hover:text-gray-600"
                            )} />
                            <span className={clsx(
                              "flex-1 text-left transition-all duration-300",
                              isCollapsed ? "hidden opacity-0 w-0" : "opacity-100 block whitespace-nowrap"
                            )}>
                              {item.name}
                            </span>
                            
                            {!isCollapsed && (
                              <ChevronDown className={clsx(
                                "w-4 h-4 transition-transform duration-200",
                                isExpanded ? "transform rotate-180" : ""
                              )} />
                            )}

                            {/* Tooltip for collapsed state */}
                            {isCollapsed && (
                              <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                                {item.name}
                              </div>
                            )}
                          </button>

                          {/* Submenu */}
                          <div className={clsx(
                            "overflow-hidden transition-all duration-300 ease-in-out",
                            isExpanded && !isCollapsed ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                          )}>
                            {item.children.map((child) => {
                              if (child.children) {
                                const isChildExpanded = expandedMenus[child.name]
                                const ChildIcon = child.icon
                                
                                return (
                                  <div key={child.name} className="space-y-1">
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleMenuClick(child);
                                      }}
                                      className={clsx(
                                        "w-full flex items-center py-2 pl-11 pr-3 text-sm font-medium rounded-lg transition-colors group relative",
                                        "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                                      )}
                                    >
                                      <ChildIcon className="w-4 h-4 mr-3 text-gray-400" />
                                      <span className="flex-1 text-left">{child.name}</span>
                                      <ChevronDown className={clsx(
                                        "w-3 h-3 transition-transform duration-200",
                                        isChildExpanded ? "transform rotate-180" : ""
                                      )} />
                                    </button>
                                    
                                    <div className={clsx(
                                      "overflow-hidden transition-all duration-300 ease-in-out",
                                      isChildExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                                    )}>
                                      {child.children.map(grandChild => {
                                         const GrandChildIcon = grandChild.icon
                                         const grandChildActive = isActive(grandChild.href)
                                         return (
                                           <Link
                                             key={grandChild.name}
                                             to={grandChild.href}
                                             onClick={onClose}
                                             className={clsx(
                                               "flex items-center py-2 pl-14 pr-3 text-sm font-medium rounded-lg transition-colors",
                                               grandChildActive
                                                 ? "text-blue-600 bg-blue-50"
                                                 : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                                             )}
                                           >
                                             <GrandChildIcon className={clsx(
                                               "w-4 h-4 mr-3",
                                               grandChildActive ? "text-blue-600" : "text-gray-400"
                                             )} />
                                             {grandChild.name}
                                           </Link>
                                         )
                                      })}
                                    </div>
                                  </div>
                                )
                              }
                              
                              const ChildIcon = child.icon
                              const childActive = isActive(child.href)
                              return (
                                <Link
                                  key={child.name}
                                  to={child.href}
                                  onClick={onClose}
                                  className={clsx(
                                    "flex items-center py-2 pl-11 pr-3 text-sm font-medium rounded-lg transition-colors",
                                    childActive
                                      ? "text-blue-600 bg-blue-50"
                                      : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                                  )}
                                >
                                  <ChildIcon className={clsx(
                                    "w-4 h-4 mr-3",
                                    childActive ? "text-blue-600" : "text-gray-400"
                                  )} />
                                  {child.name}
                                </Link>
                              )
                            })}
                          </div>
                        </div>
                      )
                    }

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={onClose}
                        title={isCollapsed ? item.name : undefined}
                        className={clsx(
                          "flex items-center py-3 text-sm font-medium rounded-xl transition-all duration-200 group relative",
                          isCollapsed ? "justify-center px-0" : "px-3",
                          active
                            ? "bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 border-r-4 border-blue-600 shadow-sm"
                            : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                        )}
                      >
                        <Icon className={clsx(
                          "w-5 h-5 flex-shrink-0 transition-colors",
                          !isCollapsed && "mr-3",
                          active ? "text-blue-600" : "text-gray-400 group-hover:text-gray-600"
                        )} />
                        <span className={clsx(
                          "flex-1 transition-all duration-300",
                          isCollapsed ? "hidden opacity-0 w-0" : "opacity-100 block"
                        )}>
                          <div className="font-medium whitespace-nowrap">{item.name}</div>
                          {item.description && !item.hideDescriptionInSidebar && (
                            <div className="text-xs text-gray-500 mt-0.5 truncate">{item.description}</div>
                          )}
                        </span>
                        
                        {/* Tooltip for collapsed state */}
                        {isCollapsed && (
                          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                            {item.name}
                          </div>
                        )}
                      </Link>
                    )
                })}
              </div>
            )}
          </nav>

          {/* Footer: User Profile & Actions */}
          <div className="border-t border-gray-200 bg-gray-50">
            <div className={clsx(
              "flex flex-col gap-2 p-3 transition-all duration-300",
              isCollapsed ? "items-center" : ""
            )}>
              {/* Profile Info */}
              <Link 
                to="/profile"
                className={clsx(
                  "flex items-center gap-3 w-full hover:bg-gray-100 p-1 rounded-lg transition-colors",
                  isCollapsed ? "justify-center" : ""
                )}
              >
                 <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-sm shrink-0 ring-2 ring-white">
                   {user?.name?.[0] || 'U'}
                 </div>
                 
                 {!isCollapsed && (
                   <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
                     <span className="text-sm font-semibold text-gray-700 truncate">{user?.name || 'User'}</span>
                     <span className="text-xs text-gray-500 truncate">{user?.role || 'Admin'}</span>
                   </div>
                 )}
              </Link>

              <div className="flex items-center w-full pt-2 border-t border-gray-200/50 mt-1 gap-2">
                <div className={clsx("flex items-center gap-1 min-w-0", isCollapsed ? "flex-col" : "")}>
                  <button
                    onClick={toggleCollapse}
                    className="hidden lg:flex p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                    title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                  >
                    {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                  </button>

                  <Link 
                    to="/settings/system" 
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Settings"
                  >
                    <Settings className="w-4 h-4" />
                  </Link>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
